import { useQuery } from "@tanstack/react-query"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Loader2 } from "lucide-react"
import { supabase } from "@/integrations/supabase/client"
import { PaymentHistory } from "@/components/payments/PaymentHistory"
import { useEffect, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { useToast } from "@/components/ui/use-toast"
import { LoadingDialog } from "@/components/ui/loading-dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

const COUNTRIES = [
  { code: "US", name: "United States" },
  { code: "IN", name: "India" },
  { code: "SG", name: "Singapore" },
] as const

type CountryCode = typeof COUNTRIES[number]["code"]

export default function PaymentsPage({ profile, session }: { profile: any | null, session: any | null }) {
  const [searchParams] = useSearchParams()
  const { toast } = useToast()
  const [isLoading, setIsLoading] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>()

  // Query for payment history
  const { data: payments = [], isLoading: isLoadingPayments } = useQuery({
    queryKey: ["payment-history"],
    queryFn: async () => {
      const { data } = await supabase
        .from("payment_settlements")
        .select("*")
        .order("created_at", { ascending: false })
      return data || []
    },
  })

  // Query for Stripe Connect account status
  const { data: connectAccount, refetch: refetchConnectAccount, isFetching } = useQuery({
    queryKey: ["stripe-connect-status"],
    queryFn: async () => {
      if (!session) {
        throw new Error('No active session found')
      }

      const response = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/hooks/stripe/connect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ action: 'get_account_status' })
      })

      if (!response.ok) {
        throw new Error('Failed to get account status')
      }

      const { data } = await response.json()
      return data
    }
  })

  // Handle Stripe Connect onboarding
  const handleStripeConnect = async () => {
    if (!selectedCountry) {
      toast({
        title: "Country Required",
        description: "Please select your country before connecting with Stripe.",
        variant: "destructive",
      })
      return
    }

    try {
      setIsLoading(true)

      const response = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/hooks/stripe/connect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          action: 'create_account_link',
          returnUrl: `${window.location.origin}/dashboard/payments`,
          refreshUrl: `${window.location.origin}/dashboard/payments`,
          country: selectedCountry
        })
      })

      if (!response.ok) {
        throw new Error('Failed to create Stripe Connect session')
      }

      const { data } = await response.json()
      window.open(data.url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Error creating Stripe Connect account:', error)
      toast({
        title: "Error",
        description: "Failed to create Stripe Connect account. Please try again.",
        variant: "destructive",
      })
      setIsLoading(false)
    }
  }

  // Handle Express dashboard access
  const handleDashboardAccess = async () => {
    try {
      setIsLoading(true)
      const { data: { session } } = await supabase.auth.getSession()
      
      if (!session) {
        throw new Error('No active session found')
      }

      const response = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/hooks/stripe/connect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ 
          action: 'create_login_link',
          accountId: connectAccount.accountId
        })
      })

      if (!response.ok) {
        throw new Error('Failed to get Stripe dashboard link')
      }

      const { data } = await response.json()
      window.location.href = data.url
    } catch (error) {
      console.error('Error accessing Stripe dashboard:', error)
      toast({
        title: "Error",
        description: "Failed to access Stripe dashboard. Please try again.",
        variant: "destructive",
      })
      setIsLoading(false)
    }
  }

  // Handle refresh from Stripe Connect
  useEffect(() => {
    if (searchParams.get('refresh') === 'true') {
      setIsRefreshing(true)
      refetchConnectAccount().finally(() => {
        setIsRefreshing(false)
      })
    }
  }, [searchParams, refetchConnectAccount])

  return (
    <div className="space-y-8">
      <h2 className="text-3xl font-bold tracking-tight">Payments</h2>
      
      <Card>
        <CardHeader>
          <CardTitle>Stripe Connect Account</CardTitle>
          <CardDescription>
            Connect your Stripe account to receive payments directly
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {connectAccount?.status === 'complete' ? (
            <div className="space-y-4">
              <p className="text-sm text-green-600">
                Your Stripe account is connected and ready to receive payments
              </p>
              <Button
                onClick={handleDashboardAccess}
                disabled={isLoading || isFetching || isRefreshing}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Redirecting to Stripe...
                  </>
                ) : isFetching || isRefreshing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Checking status...
                  </>
                ) : (
                  'Access Stripe Dashboard'
                )}
              </Button>
            </div>
          ) : connectAccount?.status === 'pending' ? (
            <div className="space-y-4">
              <p className="text-sm text-yellow-600">
                Please complete your Stripe account setup
              </p>
              <Button
                onClick={handleStripeConnect}
                disabled={isLoading || isFetching || isRefreshing}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Redirecting to Stripe...
                  </>
                ) : isFetching || isRefreshing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Checking status...
                  </>
                ) : (
                  'Complete Setup'
                )}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm">
                Connect your account with Stripe to start receiving payments
              </p>
              <div className="space-y-4">
                <Select
                  value={selectedCountry}
                  onValueChange={(value) => setSelectedCountry(value as CountryCode)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select your country" />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map((country) => (
                      <SelectItem key={country.code} value={country.code}>
                        {country.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  onClick={handleStripeConnect}
                  disabled={isLoading || isFetching || isRefreshing || !selectedCountry}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Redirecting to Stripe...
                    </>
                  ) : isFetching || isRefreshing ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Checking status...
                    </>
                  ) : (
                    'Connect with Stripe'
                  )}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Payment History</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoadingPayments ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <PaymentHistory payments={payments} />
          )}
        </CardContent>
      </Card>

      <LoadingDialog 
        open={isLoading} 
        onOpenChange={setIsLoading}
        title="Redirecting to Stripe"
        description="Please wait while we redirect you to Stripe..."
      />
    </div>
  )
}